import type { NewScoreInput } from './scores.js'

export interface SessionState {
  deckId: string
  classId: string | null
  mode: 'flashcards'
  startedAt: Date
  cardsSeen: Set<string>
  cardsCorrect: Set<string>
  missedCardIds: Set<string>
}

function copySet(values: Set<string>) {
  return new Set(values)
}

export function startSession(deckId: string, classId: string | null): SessionState {
  return {
    deckId,
    classId,
    mode: 'flashcards',
    startedAt: new Date(),
    cardsSeen: new Set<string>(),
    cardsCorrect: new Set<string>(),
    missedCardIds: new Set<string>(),
  }
}

export function markSeen(state: SessionState, cardId: string): SessionState {
  const cardsSeen = copySet(state.cardsSeen)
  cardsSeen.add(cardId)
  return { ...state, cardsSeen }
}

export function markCorrect(state: SessionState, cardId: string): SessionState {
  const cardsSeen = copySet(state.cardsSeen)
  const cardsCorrect = copySet(state.cardsCorrect)
  const missedCardIds = copySet(state.missedCardIds)
  cardsSeen.add(cardId)
  cardsCorrect.add(cardId)
  missedCardIds.delete(cardId)
  return { ...state, cardsSeen, cardsCorrect, missedCardIds }
}

export function markMissed(state: SessionState, cardId: string): SessionState {
  const cardsSeen = copySet(state.cardsSeen)
  const cardsCorrect = copySet(state.cardsCorrect)
  const missedCardIds = copySet(state.missedCardIds)
  cardsSeen.add(cardId)
  cardsCorrect.delete(cardId)
  missedCardIds.add(cardId)
  return { ...state, cardsSeen, cardsCorrect, missedCardIds }
}

export function finishSession(state: SessionState, endedAt = new Date()): NewScoreInput {
  const cardsSeen = state.cardsSeen.size
  const cardsCorrect = state.cardsCorrect.size
  const rawAccuracy = cardsSeen === 0 ? 0 : cardsCorrect / cardsSeen
  const accuracy = Math.max(0, Math.min(1, rawAccuracy))
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

  return {
    deckId: state.deckId,
    classId: state.classId,
    goalId: null,
    mode: state.mode,
    startedAt: state.startedAt.toISOString(),
    endedAt: endedAt.toISOString(),
    durationSeconds: Math.max(
      0,
      Math.floor((endedAt.getTime() - state.startedAt.getTime()) / 1000),
    ),
    cardsSeen,
    cardsCorrect,
    accuracy,
    missedCardIds: Array.from(state.missedCardIds),
    clientTimezone: timezone,
  }
}
